// Identifiants des poteaux : traçabilité d'un numéro et numérotation du stock existant.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid, InputAdornment, Link, Snackbar,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { SearchOutlined, QrCode2Outlined, PinOutlined } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Kpi, Panel } from '../components/ui';
import { useAuth } from '../context/AuthContext';
import { poteauxAPI } from '../services/api';
import { apiError, dateFr } from '../utils/format';
import { compactPoles, expandPoles } from '../utils/poteaux';
import { tokens } from '../theme';

const ETAT = {
  LIVRE: 'success', BL_EN_COURS: 'warning', MATURE: 'info', EN_MATURATION: 'default', SORTI: 'default',
};
const SOURCE = { RAPPORT: 'Rapport journalier', MOUVEMENT: 'Mouvement de stock', INVENTAIRE: 'Inventaire physique' };
const BL_STATUT = { EN_COURS: 'Brouillon', VALIDEE: 'Scellé', ANNULEE: 'Annulé' };

const Ligne = ({ label, children }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '170px 1fr' }, gap: { xs: 0, sm: 2 }, py: 0.9, borderBottom: '1px solid', borderColor: 'divider' }}>
    <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{label}</Typography>
    <Box sx={{ fontSize: 14 }}>{children}</Box>
  </Box>
);

const Fiche = ({ r, canCompta }) => {
  const navigate = useNavigate();
  const p = r.production;
  return (
    <Panel sx={{ mb: 2 }}
      title={<>Poteau <span style={{ fontFamily: tokens.mono }}>{r.numero}</span> · {r.pba_type}{p?.pba_description ? <Typography component="span" sx={{ color: 'text.secondary', fontSize: 14 }}> — {p.pba_description}</Typography> : null}</>}
      action={r.etat && <Chip label={r.etat.label} color={ETAT[r.etat.code]} variant={r.etat.code === 'LIVRE' ? 'filled' : 'outlined'} />}>
      {r.alerte && <Alert severity="warning" sx={{ mb: 1 }}>{r.alerte}</Alert>}
      {p && (
        <>
          <Ligne label="Production">
            {dateFr(p.production_date)} · {SOURCE[p.source] || p.source}
            {p.report_id && <> · <Link component="button" onClick={() => window.open(`/rapports/${p.report_id}/imprimer`, '_blank')}>RAP-{String(p.report_id).padStart(5, '0')}</Link></>}
            {p.rapport_auteur && <> · {p.rapport_auteur}</>}
          </Ligne>
          <Ligne label="Lot">{p.reference || `LOT-${p.lot_id}`}</Ligne>
          <Ligne label="Maturité">{dateFr(p.maturation_date)}</Ligne>
          <Ligne label="Numéro enregistré">{p.saisi_par || '—'}</Ligne>
        </>
      )}
      <Ligne label="Livraison">
        {r.livraisons.length === 0 ? <span style={{ color: '#5B6675' }}>Pas encore livré</span> : r.livraisons.map((b) => (
          <Box key={b.delivery_id} sx={{ mb: 0.5 }}>
            <Link component="button" onClick={() => navigate(`/bons-livraison/${b.delivery_id}`)} sx={{ fontFamily: tokens.mono, fontWeight: 600 }}>{b.bl_numero}</Link>
            {' '}· {dateFr(b.delivery_date)} · <b>{b.client}</b>{b.destination ? ` (${b.destination})` : ''} · {BL_STATUT[b.status]}
            {b.factures.length > 0 && (
              <Box sx={{ mt: 0.25 }}>Facture : {b.factures.map((f) => (canCompta
                ? <Link key={f.id} component="button" onClick={() => navigate(`/factures/${f.id}`)} sx={{ fontFamily: tokens.mono, mr: 1 }}>{f.numero || 'brouillon'}</Link>
                : <span key={f.id} style={{ fontFamily: tokens.mono, marginRight: 8 }}>{f.numero || 'brouillon'}</span>))}
              </Box>
            )}
          </Box>
        ))}
      </Ligne>
    </Panel>
  );
};

const Poteaux = () => {
  const { canNumeroter, canCompta } = useAuth();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get('q') || '');
  const [res, setRes] = useState(null);
  const [error, setError] = useState('');
  const [stock, setStock] = useState(null);
  const [dlg, setDlg] = useState(null);
  const [toast, setToast] = useState('');

  const search = useCallback((value) => {
    const v = String(value || '').trim();
    if (!v) return;
    setError('');
    setParams({ q: v }, { replace: true });
    poteauxAPI.recherche(v).then((r) => setRes(r.data)).catch((e) => setError(apiError(e)));
  }, [setParams]);
  useEffect(() => { if (params.get('q')) search(params.get('q')); }, []); // eslint-disable-line

  const loadStock = useCallback(() => {
    if (canNumeroter) poteauxAPI.aNumeroter().then((r) => setStock(r.data)).catch((e) => setError(apiError(e)));
  }, [canNumeroter]);
  useEffect(() => { loadStock(); }, [loadStock]);

  const openDlg = (lot) => {
    const d = stock.suggestions[lot.pba_type];
    setDlg({ lot, texte: d ? (lot.manquants === 1 ? String(d) : `${d} à ${d + lot.manquants - 1}`) : '', error: '' });
  };
  const check = dlg ? expandPoles(dlg.texte) : null;
  const dlgOk = dlg && !check.error && !check.dups.length && check.list.length > 0 && check.list.length <= dlg.lot.manquants;
  const save = async () => {
    try {
      const r = await poteauxAPI.attribuer(dlg.lot.id, dlg.texte);
      setDlg(null); setToast(r.data.message); loadStock();
    } catch (e) { setDlg({ ...dlg, error: apiError(e) }); }
  };

  return (
    <Layout title="Identifiants des poteaux" plain>
      <Panel title="Traçabilité d'un poteau" sx={{ mb: 2 }}>
        <Box component="form" onSubmit={(e) => { e.preventDefault(); search(q); }} sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <TextField size="small" label="Numéro du poteau" value={q} onChange={(e) => setQ(e.target.value)} autoFocus sx={{ flex: '1 1 240px', maxWidth: 360 }}
            placeholder="ex. 30005"
            InputProps={{ startAdornment: <InputAdornment position="start"><QrCode2Outlined fontSize="small" /></InputAdornment> }} />
          <Button type="submit" variant="contained" startIcon={<SearchOutlined />}>Rechercher</Button>
        </Box>
        <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 1 }}>
          Production, rapport, maturité, bon de livraison, client et facture du poteau.
        </Typography>
      </Panel>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {res && res.resultats.map((r) => <Fiche key={`${r.pba_type}-${r.numero}`} r={r} canCompta={canCompta} />)}
      {res && !res.resultats.length && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Aucun poteau ne porte le numéro « {res.q} ».
          {res.suggestions.length > 0 && (
            <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              Numéros proches :
              {res.suggestions.map((s) => <Chip key={`${s.pba_type}-${s.numero}`} size="small" label={`${s.numero} (${s.pba_type})`} onClick={() => { setQ(s.numero); search(s.numero); }} />)}
            </Box>
          )}
        </Alert>
      )}

      {canNumeroter && stock && (
        <>
          <Grid container spacing={2} sx={{ mb: 2, mt: 0 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Kpi icon={PinOutlined} tint={stock.total ? 'amber' : 'green'} label="Poteaux en stock sans numéro" value={stock.total}
                sub={stock.total ? `${stock.lots.length} lot(s) à compléter` : 'Tout le stock est numéroté'} />
            </Grid>
          </Grid>
          {stock.lots.length > 0 && (
            <Panel title="Stock à numéroter" flush
              action={<Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Poteaux produits avant la numérotation, ou entrés par inventaire</Typography>}>
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell><TableCell>Lot</TableCell><TableCell>Production</TableCell>
                      <TableCell align="right">En stock</TableCell><TableCell align="right">Sans numéro</TableCell><TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stock.lots.map((l) => (
                      <TableRow key={l.id} hover>
                        <TableCell sx={{ fontWeight: 600 }}>{l.pba_type}</TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>{l.reference || `LOT-${l.id}`} · {SOURCE[l.source] || l.source}</TableCell>
                        <TableCell>{dateFr(l.production_date)}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: tokens.mono }}>{l.en_stock}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: tokens.mono, fontWeight: 700, color: 'warning.main' }}>{l.manquants}</TableCell>
                        <TableCell align="right"><Button size="small" variant="outlined" onClick={() => openDlg(l)}>Attribuer les numéros</Button></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Panel>
          )}
        </>
      )}

      <Dialog open={!!dlg} onClose={() => setDlg(null)} fullWidth maxWidth="sm">
        {dlg && (
          <>
            <DialogTitle>Numéros — {dlg.lot.pba_type}, lot du {dateFr(dlg.lot.production_date)}</DialogTitle>
            <DialogContent sx={{ pt: '8px !important' }}>
              {dlg.error && <Alert severity="error" sx={{ mb: 2 }}>{dlg.error}</Alert>}
              <Typography sx={{ fontSize: 14, mb: 2 }}>
                {dlg.lot.manquants} poteau(x) en stock sans numéro. Saisissez les numéros marqués sur les poteaux
                (plage « 30000 à 30049 » ou liste « 30000, 30002 »). Vous pouvez en saisir une partie maintenant et le reste plus tard.
              </Typography>
              <TextField fullWidth multiline minRows={2} label="Numéros" value={dlg.texte} onChange={(e) => setDlg({ ...dlg, texte: e.target.value, error: '' })}
                error={!!dlg.texte && !dlgOk}
                helperText={check.error || (check.dups.length ? `En double : ${check.dups.slice(0, 5).join(', ')}` : '')
                  || (check.list.length > dlg.lot.manquants ? `${check.list.length} numéros pour ${dlg.lot.manquants} poteau(x) sans numéro`
                    : check.list.length ? `${check.list.length} numéro(s) : ${compactPoles(check.list)}` : '')} />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDlg(null)}>Annuler</Button>
              <Button variant="contained" disabled={!dlgOk} onClick={save}>Enregistrer</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast('')} message={toast} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Layout>
  );
};

export default Poteaux;
